/**
 * Inline Code Tool for Editor.js
 *
 * codex-team/editor.js 생태계의 @editor-js/inline-code
 * (https://github.com/editor-js/inline-code) 를 프로젝트에 맞게 이식한 버전.
 *
 * 선택한 텍스트를 `<code class="inline-code">` 로 감싸 인라인 코드 서식을 적용한다.
 * - 스타일: app/javascript/stylesheets/_inline_code.scss (다크모드 대응)
 * - 이메일 렌더링: app/helpers/editor_js/email.rb 의 parse_email_text 에서
 *   `<code>` 에 인라인 스타일을 주입해 웹과 동일한 경험을 제공한다.
 */
export default class InlineCode {
  /**
   * Editor.js 에 인라인 도구임을 알린다.
   */
  static get isInline() {
    return true;
  }

  /**
   * 인라인 툴바에 표시되는 도구 이름
   */
  static get title() {
    return '인라인 코드';
  }

  /**
   * @param {{api: object}} options
   */
  constructor({ api }) {
    this.api = api;

    this.button = null;
    this.tag = 'CODE';
    this.class = 'inline-code';

    this.iconClasses = {
      base: this.api.styles.inlineToolButton,
      active: this.api.styles.inlineToolButtonActive,
    };
  }

  /**
   * 인라인 툴바 버튼 생성
   * @returns {HTMLElement}
   */
  render() {
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.classList.add(this.iconClasses.base);
    this.button.innerHTML = this.toolboxIcon;

    return this.button;
  }

  /**
   * 선택 영역에 서식 토글
   * @param {Range} range
   */
  surround(range) {
    if (!range) {
      return;
    }

    const termWrapper = this.api.selection.findParentTag(this.tag, this.class);

    if (termWrapper) {
      this.unwrap(termWrapper);
    } else {
      this.wrap(range);
    }
  }

  /**
   * 선택 텍스트를 code 태그로 감싼다
   * @param {Range} range
   */
  wrap(range) {
    const selectedText = range.extractContents();
    const codeElement = document.createElement(this.tag);

    codeElement.classList.add(this.class);
    codeElement.appendChild(selectedText);
    range.insertNode(codeElement);

    this.api.selection.expandToTag(codeElement);
  }

  /**
   * code 태그를 해제한다
   * @param {HTMLElement} termWrapper
   */
  unwrap(termWrapper) {
    this.api.selection.expandToTag(termWrapper);

    const sel = window.getSelection();
    const range = sel.getRangeAt(0);
    const unwrappedContent = range.extractContents();

    termWrapper.parentNode.removeChild(termWrapper);
    range.insertNode(unwrappedContent);

    sel.removeAllRanges();
    sel.addRange(range);
  }

  /**
   * 현재 커서 위치가 인라인 코드인지에 따라 버튼 활성 상태 갱신
   */
  checkState() {
    const termTag = this.api.selection.findParentTag(this.tag, this.class);

    if (this.button) {
      this.button.classList.toggle(this.iconClasses.active, !!termTag);
    }
  }

  /**
   * 툴바 아이콘 (</> 브라켓)
   */
  get toolboxIcon() {
    return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>';
  }

  /**
   * 저장 시 허용할 태그/속성. class 를 보존해 렌더링/이메일 변환과 일치시킨다.
   */
  static get sanitize() {
    return {
      code: {
        class: 'inline-code',
      },
    };
  }
}
